import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { NewInput } from '../../PIM/common';
import useDeferredRowChange from '../useDeferredRowChange';
import { sanitizeNumericInput } from '../numericInput';
import type { BombiixCellRendererParams, BombiixRowId } from '../types';

interface CurrencyInputCellExtras {
  /**
   * Opt-in: text rendered in brackets after the field, e.g. the `(5 %)` rate a derived royalty fee
   * came from. Omit it — or return nullish — and the cell renders exactly as it always has, so
   * callers that do not pass it are unaffected.
   */
  noteAccessor?: (row: any) => string | null | undefined;
  /**
   * Class applied to the field only while a note sits beside it, so the two share the cell width.
   * Caller-controlled; ignored entirely when no note is rendered.
   */
  pairedFieldClassName?: string;
  /** Accept a leading `-`. Defaults to true; pass false for a column that is never negative. */
  allowNegative?: boolean;
  /** Cap the digits after the decimal point. Omitted, the fraction is uncapped. */
  decimals?: number;
}

/**
 * Currency-prefixed text cell backed by the PIM NewInput control. The `£` (or
 * caller-supplied) prefix sits inside `.cag-affix-field` next to NewInput.
 */
function CurrencyInputCell(params: BombiixCellRendererParams<any, string> & CurrencyInputCellExtras) {
  const {
    value,
    data,
    colDef,
    onRowChange,
    getRowId,
    currencySymbol = '£',
    placeholder,
    disabled,
    readOnly,
    error,
    noteAccessor,
    pairedFieldClassName = 'w-80px flex-shrink-0',
    allowNegative,
    decimals,
  } = params;
  const rowId: BombiixRowId | undefined = getRowId ? getRowId(data) : (data as { id?: BombiixRowId } | undefined)?.id;
  const field = colDef?.field;

  const focusedRef = useRef<boolean>(false);
  const [local, setLocal] = useState<string>(value ?? '');
  const [commitChange, flushChange] = useDeferredRowChange(onRowChange);

  useEffect(() => {
    if (!focusedRef.current) setLocal(value ?? '');
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const cleaned = sanitizeNumericInput(e.target.value, { allowNegative, decimals });
    setLocal(cleaned);
    if (rowId !== undefined && field) commitChange(rowId, field, cleaned);
  };

  const note = typeof noteAccessor === 'function' && data ? noteAccessor(data) : undefined;
  const hasNote = note !== undefined && note !== null && note !== '';

  return (
    <div className={`cag-cell-wrap${error ? ' cag-cell-wrap--error' : ''}`}>
      <div className="cag-cell">
        <div className={`cag-affix-field${hasNote ? ` ${pairedFieldClassName}` : ''}${disabled || readOnly ? ' cag-affix-field-disabled' : ''}`}>
          <span className="cag-affix cag-affix-prefix force-ltr">{currencySymbol}</span>
          <NewInput
            className="cag-affix-input"
            type="text"
            inputMode="decimal"
            invalid={Boolean(error)}
            value={local}
            placeholder={placeholder ?? colDef?.headerName ?? 'Text Box'}
            disabled={disabled}
            readOnly={readOnly}
            onFocus={() => {
              focusedRef.current = true;
            }}
            onBlur={() => {
              focusedRef.current = false;
              flushChange();
              setLocal(value ?? '');
            }}
            onChange={handleChange}
          />
        </div>
        {hasNote ? <span className="text-nowrap ml-1">{`(${note})`}</span> : null}
      </div>
      {error ? <span className="cag-cell-error">{error}</span> : null}
    </div>
  );
}

export default CurrencyInputCell;
